import type { AccessRight } from "../shared/auth/accessRights";
import { hasAccessRight } from "../shared/auth/accessRights";

/**
 * Access rights that open a restricted screen; any one of them is enough. Each list contains the
 * right of the screen's navigation entry, so the navigation never leads to „Keine Berechtigung“,
 * plus the rights for which the screen offers a function reached otherwise: project responsibles
 * see „Projekte“ in the navigation while they are responsible for a project (T-125, F-74), HR
 * follows the export link of the service banner to „Mandant“ (T-138) and maintains the leave rules
 * there (T-161), and readers of employee times see their team.
 *
 * This only decides what the interface shows. The server checks every request on its own.
 */
export const screenAccess = {
  ownRecords: ["AccessOwnRecords"],
  team: ["ApproveCorrections", "ReadEmployeeTimes"],
  violations: ["ManageViolations"],
  projects: ["ManageProjects", "AccessOwnRecords"],
  masterData: ["ManageMasterData"],
  tenant: ["ManageTenant", "ExportTenantData", "ManageMasterData"],
  billing: ["ReadSubscription"],
} as const satisfies Readonly<Record<string, readonly AccessRight[]>>;

/** Whether a restricted screen may be shown. */
export type RouteAccess = "granted" | "denied" | "unknown";

/**
 * Decides whether a restricted screen opened by a link or an address may be shown (UX-025).
 *
 * @param roles - Roles from `GET /me` or the last known roles (F-05), or `undefined` if unknown.
 * @param anyOf - Rights of which one opens the screen.
 * @returns `unknown` while the roles are not known, otherwise `granted` or `denied`.
 */
export function routeAccess(
  roles: readonly string[] | undefined,
  anyOf: readonly AccessRight[],
): RouteAccess {
  let access: RouteAccess = "unknown";

  if (roles !== undefined) {
    access = anyOf.some((right) => hasAccessRight(roles, right)) ? "granted" : "denied";
  }

  return access;
}
